import { useEffect, useRef } from "react"

/**
 * The session-gating logic behind the dashboard's auth gate.
 *
 * Presentation deliberately stays in the app. What lives here is the part that
 * would be dangerous to have two copies of once Contract Maker becomes its own
 * deployable app: deciding when a session is about to lapse, telling a lapsed
 * session apart from a visitor who was never signed in, and scrubbing the
 * verification parameters out of the address bar. A second app rendering its
 * own dialogs is harmless; a second app disagreeing about when a session has
 * expired is not.
 *
 * Everything here is React-and-nothing-else on purpose, so sharing the gate
 * never drags a UI library into the auth package.
 */

/**
 * Tracks whether this visitor was ever signed in during the current mount.
 *
 * Distinguishes a session that ran out from someone who simply arrived signed
 * out. Only the former should be told their session expired; the latter is
 * sent to the login page without an alarming dialog.
 */
export function useHasBeenAuthenticated(isAuthenticated: boolean): boolean {
  const hasBeenAuthenticatedRef = useRef(false)
  if (isAuthenticated) hasBeenAuthenticatedRef.current = true
  return hasBeenAuthenticatedRef.current
}

/**
 * Calls `onChange` when the expiry warning should be shown or hidden.
 *
 * Fires immediately when the session is already inside the warning window,
 * because a timer scheduled with a negative delay would otherwise fire at once
 * anyway and a session restored from storage can arrive already close to
 * lapsing.
 */
